import React, { useState } from "react";
import { toast } from "sonner";
import { BrutalButton, BrutalInput } from "./Brutal";

/**
 * Capture the per-unit sale_price + cash collected when closing a
 * receivable. Pre-fills price from the best signal we already have:
 * explicit sale_price → SRP → wholesale → blank.
 *
 * Props:
 *  - target:    the receivable row { id, product_name, quantity, unit,
 *                 unit_price, suggested_retail_price, wholesale_price,
 *                 to_user_name }
 *  - busy:      bool — true while the POST is in-flight (disables buttons)
 *  - onCancel:  () => void
 *  - onConfirm: (body) => void   body = { sale_price?, amount_paid? }
 *  - t:         i18next translator
 */
export default function MarkPaidDialog({ target, busy, onCancel, onConfirm, t }) {
  const initialPrice =
    target.unit_price && target.unit_price > 0
      ? String(target.unit_price)
      : target.suggested_retail_price != null
        ? String(target.suggested_retail_price)
        : target.wholesale_price != null
          ? String(target.wholesale_price)
          : "";
  const [pricePerUnit, setPricePerUnit] = useState(initialPrice);
  // Track whether the user has manually edited the total; otherwise keep
  // it auto-derived from qty × price (the common case).
  const [amountPaid, setAmountPaid] = useState(
    initialPrice ? String(Number(initialPrice) * target.quantity) : ""
  );
  const [amountEdited, setAmountEdited] = useState(false);

  const handlePrice = (v) => {
    setPricePerUnit(v);
    if (!amountEdited) {
      const num = Number(v);
      setAmountPaid(
        Number.isFinite(num) && num >= 0 ? String(+(num * target.quantity).toFixed(2)) : ""
      );
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const price = pricePerUnit === "" ? null : Number(pricePerUnit);
    if (price !== null && (!Number.isFinite(price) || price < 0)) {
      toast.error(t("receivables.invalid_price"));
      return;
    }
    const paid = amountPaid === "" ? null : Number(amountPaid);
    if (paid !== null && (!Number.isFinite(paid) || paid < 0)) {
      toast.error(t("receivables.invalid_amount"));
      return;
    }
    const body = {};
    if (price !== null) body.sale_price = price;
    if (paid !== null) body.amount_paid = paid;
    onConfirm(body);
  };

  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const total = Number(pricePerUnit) * target.quantity;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="mark-paid-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">{t("receivables.mark_paid_title")}</h2>
        <p className="text-sm text-zinc-600 mt-1 mb-4">
          {t("receivables.mark_paid_subtitle", {
            product: target.product_name,
            qty: target.quantity,
            unit: target.unit || "units",
            distributor: target.to_user_name || "",
          })}
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("receivables.price_per_unit", { unit: target.unit || t("common.units") })}
            </label>
            <BrutalInput
              type="number"
              min="0"
              step="0.01"
              value={pricePerUnit}
              onChange={(e) => handlePrice(e.target.value)}
              placeholder={
                target.wholesale_price != null ? `≥ ${target.wholesale_price} (wholesale)` : "0.00"
              }
              data-testid="mark-paid-price-input"
              autoFocus
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              {target.wholesale_price != null
                ? t("receivables.price_hint_with_wholesale", {
                    wholesale: fmt$(target.wholesale_price),
                  })
                : t("receivables.price_hint_no_wholesale")}
            </p>
          </div>

          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex items-baseline justify-between">
            <span className="text-xs text-zinc-600">{t("receivables.implied_total")}</span>
            <span
              className="text-base font-semibold tabular-nums"
              data-testid="mark-paid-implied-total"
            >
              {Number.isFinite(total) ? fmt$(total) : "—"}
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("receivables.amount_collected")}
            </label>
            <BrutalInput
              type="number"
              min="0"
              step="0.01"
              value={amountPaid}
              onChange={(e) => {
                setAmountPaid(e.target.value);
                setAmountEdited(true);
              }}
              placeholder={Number.isFinite(total) ? String(total.toFixed(2)) : "0.00"}
              data-testid="mark-paid-amount-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">{t("receivables.amount_hint")}</p>
          </div>

          <div className="flex gap-3 pt-2">
            <BrutalButton
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onCancel}
              disabled={busy}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={busy}
              data-testid="mark-paid-confirm"
            >
              {busy ? t("assign.sending") : t("receivables.confirm_paid")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}
